// =============================================================================
// features/assistant/components/AssistantTurnItem.tsx — Um turno de conversa
// do copiloto interno (F6-S09/F6-S12): pergunta + resposta (ou loading/erro).
//
// Estados: pending (skeleton — nunca spinner sozinho), success (narrativa em
// markdown + cards de dados por bloco — F6-S22 — com fallback ao texto plano
// legado + fontes citadas via sources[]), error (alerta + retry).
// =============================================================================

import * as React from 'react';

import { cn } from '../../../lib/cn';
import type { AssistantTurn } from '../types';

import { AssistantAnswerGroup } from './AssistantAnswerGroup';

interface AssistantTurnItemProps {
  turn: AssistantTurn;
  onRetry: (turn: AssistantTurn) => void;
  /**
   * Passo atual do agente, vindo do socket. Só é usado no estado `pending`.
   * `null` = sem passo conhecido (socket fora, ou resposta sem ferramenta).
   */
  progressLabel?: string | null | undefined;
}

// ── Bolha da pergunta do usuário ──────────────────────────────────────────────

function QuestionBubble({ question }: { question: string }): React.JSX.Element {
  return (
    <div className="flex justify-end">
      <div
        className="max-w-[85%] rounded-md px-[14px] py-[10px]"
        style={{
          background: 'var(--grad-azul)',
          color: 'var(--text-on-brand)',
          boxShadow: 'var(--elev-1)',
        }}
      >
        <p className="font-sans text-sm font-medium whitespace-pre-wrap break-words">{question}</p>
      </div>
    </div>
  );
}

// ── Skeleton "consultando dados" (nunca spinner sozinho — DS §13) ────────────

/**
 * Estado de espera do turno.
 *
 * `progressLabel` vem do socket e conta o que o agente está fazendo AGORA
 * ("Lendo a conversa do cliente"). Sem ele, cai no texto genérico anterior —
 * é o caso de socket desconectado ou pergunta que não usa ferramenta.
 *
 * A espera aqui chega a dezenas de segundos (o agente encadeia chamadas de LLM),
 * e skeleton parado é indistinguível de travado. Só o texto pulsa: animar a
 * caixa inteira a cada troca de passo faria a tela piscar.
 */
function AnswerSkeleton({ progressLabel }: { progressLabel: string | null }): React.JSX.Element {
  return (
    <div
      className="max-w-[85%] rounded-md border px-[14px] py-3"
      style={{
        background: 'var(--bg-elev-1)',
        borderColor: 'var(--border-subtle)',
        boxShadow: 'var(--elev-1)',
      }}
      aria-live="polite"
      aria-busy="true"
    >
      <p className="font-sans text-xs text-ink-3 mb-2 animate-pulse">
        {progressLabel ?? 'Consultando seus dados'}…
      </p>
      <div className="flex flex-col gap-1.5 animate-pulse">
        <div className="h-3 w-56 rounded-xs" style={{ background: 'var(--surface-muted)' }} />
        <div className="h-3 w-40 rounded-xs" style={{ background: 'var(--surface-muted)' }} />
      </div>
    </div>
  );
}

// ── Erro com retry ────────────────────────────────────────────────────────────

function ErrorBubble({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}): React.JSX.Element {
  return (
    <div
      className="max-w-[85%] rounded-md px-[14px] py-3"
      style={{
        background: 'var(--danger-bg)',
        borderLeft: '3px solid var(--danger)',
        boxShadow: 'var(--elev-1)',
      }}
    >
      <p className="font-sans text-sm font-medium" style={{ color: 'var(--danger)' }}>
        {message}
      </p>
      <button
        type="button"
        onClick={onRetry}
        className={cn(
          'mt-2 font-sans text-xs font-semibold underline underline-offset-2',
          'transition-opacity duration-fast ease hover:opacity-70',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-azul/40 rounded-xs',
        )}
        style={{ color: 'var(--danger)' }}
      >
        Tentar novamente
      </button>
    </div>
  );
}

// ── Turno completo ─────────────────────────────────────────────────────────────

export function AssistantTurnItem({
  turn,
  onRetry,
  progressLabel,
}: AssistantTurnItemProps): React.JSX.Element {
  return (
    <div className="flex flex-col gap-2">
      <QuestionBubble question={turn.question} />

      <div className="flex justify-start">
        {turn.status === 'pending' && <AnswerSkeleton progressLabel={progressLabel ?? null} />}
        {turn.status === 'success' && (
          <AssistantAnswerGroup
            narrative={turn.narrative}
            blocks={turn.blocks}
            sources={turn.sources ?? []}
            legacyAnswer={turn.answer ?? ''}
          />
        )}
        {turn.status === 'error' && (
          <ErrorBubble
            message={turn.errorMessage ?? 'Não foi possível obter resposta.'}
            onRetry={() => onRetry(turn)}
          />
        )}
      </div>
    </div>
  );
}
